import BitsyApollo from './BitsyApollo';
import Features from './Features';

/* The add-ons offered in the cart drawer and after the order.

   getPromoProductsForPostPurchase cannot answer here: it builds its result
   inside `if (data.purchaseId)` and returns an empty array without one
   (product.directives.js:972-989), and there is no purchase at this point any
   more. Its selection rule is not the purchase's though — a flag, a window
   and stock — so the same set is reachable from the catalogue, and the
   purchase it filtered against becomes the cart.

   Ordering is the one thing not reproduced: the resolver sorts on
   ownPromoData.displayOrder, which the GraphQL type does not expose
   (product.def.js:117-138). getProducts returns its own displayOrder order. */

/* Eligibility is the tag, not ownPromoData.isPostPurchasePromo: the journal is
   in the tag and carries no promo flags. Which window a tagged product lands in
   is the Statsig config's. A description matching no Tag makes the resolver
   answer [] with no error (product.directives.js:128). */
const UPSELL_TAG = 'consumer-addon';

// Declaration order is priority: an id listed under two windows lands in the first.
export const WINDOWS = {
  CART_DRAWER: 'cartDrawer',
  POST_PURCHASE: 'postPurchase',
};
const WINDOW_ORDER = Object.values(WINDOWS);

const WINDOWS_CONFIG = 'consumer-upsell-windows';

/* Launch layout, used until Statsig answers or when the config is absent. Both
   journals, current first: an id the tag does not return is skipped, so the
   older one comes back to the drawer by being re-tagged, with no code change. */
const DEFAULT_WINDOWS_CONFIG = {
  defaultWindow: WINDOWS.POST_PURCHASE,
  windows: {
    [WINDOWS.CART_DRAWER]: [
      '6a85db6268f10a1422ee1bec',
      '64af0d1e411cdf0b9bd40666',
    ],
  },
};

// Ceiling, not fetch size. Below the tag's size, offers past the cut go missing
// with no error. $skip is offset * limit — don't add an offset without reading
// mongoAggregation.lib.js:223.
const LIMIT = 5000;

/* Truncation is how this read fails, and it fails looking healthy: a valid
   response of LIMIT products with the promos missing from it. */
const warnIfTruncated = (products) => {
  const total = products[0]?.totalNumberOfProducts;
  if (total > products.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[upsellAddOns] got ${products.length} of ${total} products; raise LIMIT — upsell offers past the cut are silently missing.`,
    );
  }
};

// An empty read is the only sign the tag was renamed or dropped; the resolver
// answers [] for a missing tag exactly as it does for an empty one.
const warnIfUntagged = (products) => {
  if (!products.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[upsellAddOns] nothing carries the "${UPSELL_TAG}" tag; no window will offer anything. Check the tag still exists.`,
    );
  }
};

const asTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const hasWindow = (promo) => !!(promo?.validOn || promo?.expiresOn);

/* Both bounds required once there is a window at all: the resolver queries
   `validOn: { $lte: now }` and `expiresOn: { $gte: now }`, and Mongo brackets
   comparisons by BSON type, so a half-open promo never came back from it. */
const isLive = (promo, now) => {
  const from = asTime(promo?.validOn);
  const until = asTime(promo?.expiresOn);
  if (from === null || until === null) return false;
  return now >= from && now <= until;
};

/* What the tag cannot carry, because both change without anyone editing a
   product: stock, and where a promo sits in its window. No window at all is
   evergreen — the journal, which is on no calendar. */
export const isUpsellAddOn = (product, now = Date.now()) => {
  if (!product || product.isOutOfStock) return false;
  return hasWindow(product.ownPromoData)
    ? isLive(product.ownPromoData, now)
    : true;
};

/* What the purchase used to rule out, the cart rules out instead — and in a
   drawer that is the better question, because it answers again every time the
   shopper adds something. */
const idsInCart = (cartInfo) =>
  new Set(
    (cartInfo?.productsAndDetails || [])
      .map((entry) => entry?.product?._id)
      .filter(Boolean),
  );

/* A vitamin subscriber is offered vitamin promos and nobody else is, unless
   that leaves nothing to offer. Evergreen add-ons sit outside that rule and go
   first, which is where the journal was while it was fetched on its own. */
const forCart = (products, cartInfo) => {
  if (!products.length) return [];
  const isVitaminSub = cartInfo?.subscription?.plan?.type === 'VITAMINS';

  const evergreen = products.filter((p) => !hasWindow(p.ownPromoData));
  const promos = products.filter((p) => hasWindow(p.ownPromoData));

  const matching = promos.filter((product) =>
    isVitaminSub
      ? product.ownPromoData?.isVitaminPromo
      : !product.ownPromoData?.isVitaminPromo,
  );

  return [
    ...evergreen,
    ...(isVitaminSub && !matching.length ? promos : matching),
  ];
};

export const selectUpsellAddOns = (products, cartInfo, now = Date.now()) => {
  if (!Array.isArray(products)) return [];
  const inCart = idsInCart(cartInfo);
  return forCart(
    products.filter(
      (product) => isUpsellAddOn(product, now) && !inCart.has(product._id),
    ),
    cartInfo,
  );
};

/* The catalogue read, kept apart from the choosing so the drawer can hold the
   products it fetched and choose again against a cart that has just changed
   without asking twice. It takes `now` for the same reason the chooser does —
   a clock read inside here is one a test cannot set. */
export const fetchUpsellCatalogue = async (now = Date.now()) => {
  try {
    const products = await BitsyApollo.getUnifiedCheckoutUpsellProducts({
      tagDescription: UPSELL_TAG,
      limit: LIMIT,
    });
    if (!Array.isArray(products)) return [];
    warnIfUntagged(products);
    warnIfTruncated(products);
    return products.filter((product) => isUpsellAddOn(product, now));
  } catch {
    // An offer whose products cannot be read is not made.
    return [];
  }
};

export const readWindowsConfig = () => {
  try {
    const value = Features.getConfig(WINDOWS_CONFIG)?.value;
    if (!('windows' in value) && !('defaultWindow' in value)) {
      return DEFAULT_WINDOWS_CONFIG;
    }
    return value;
  } catch {
    return DEFAULT_WINDOWS_CONFIG;
  }
};

/* Each product lands in exactly one window: the first, in WINDOW_ORDER, whose
   list names it, otherwise the default. Listed ids the tag did not return are
   ignored, since the tag is what makes a product eligible. */
export const splitUpsellWindows = (products, config) => {
  const split = Object.fromEntries(WINDOW_ORDER.map((name) => [name, []]));
  if (!Array.isArray(products)) return split;

  const windows = config?.windows ?? {};
  const defaultWindow = WINDOW_ORDER.includes(config?.defaultWindow)
    ? config.defaultWindow
    : WINDOWS.POST_PURCHASE;

  const byId = new Map(products.map((product) => [product._id, product]));
  const placed = new Set();

  WINDOW_ORDER.forEach((name) => {
    const ids = Array.isArray(windows[name]) ? windows[name] : [];
    ids.forEach((id) => {
      if (typeof id !== 'string' || !byId.has(id)) return;
      if (placed.has(id)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[upsellAddOns] "${id}" is listed in more than one window of ${WINDOWS_CONFIG}; showing it only in the first.`,
        );
        return;
      }
      placed.add(id);
      split[name].push(byId.get(id));
    });
  });

  split[defaultWindow].push(
    ...products.filter((product) => !placed.has(product._id)),
  );
  return split;
};

export const fetchUpsellWindows = async (now = Date.now()) =>
  splitUpsellWindows(await fetchUpsellCatalogue(now), readWindowsConfig());

/* What an add-on costs and what it is struck against, in one place because
   both placements have to quote the same number — a price that differed
   between the arms would be a confound in the experiment comparing them.
   `originalCost` is the shelf price and the only thing worth striking: the
   promo cost is often absent, and striking `actualCost` against itself is how
   the post-purchase modal came to show a slash on one product only. */
export const upsellPricing = (product = {}) => {
  const cost = product.ownPromoData?.cost ?? product.actualCost;
  const wasPrice = product.originalCost;
  return { cost, wasPrice: wasPrice > cost ? wasPrice : null };
};

export default {
  WINDOWS,
  isUpsellAddOn,
  selectUpsellAddOns,
  fetchUpsellCatalogue,
  readWindowsConfig,
  splitUpsellWindows,
  fetchUpsellWindows,
  upsellPricing,
};
